import React, { useEffect, useReducer, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Edit2, X, Save, Warehouse, Phone, MessageCircle } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import RequireAdmin from "@/components/admin/RequireAdmin";
import { useAdminI18n } from "@/lib/adminI18n";

const COUNTRY_OPTS = [
  ["LB", "Lebanon"],   ["TR", "Turkey"], ["UK", "United Kingdom"],
  ["FR", "France"],    ["AE", "UAE"],    ["CN", "China"],  ["US", "USA"],
];

const METHOD_OPTS = [
  ["office", "Office / Customer Contact"],
  ["air", "Air"],
  ["sea", "Sea"],
  ["hk_air", "HK Air"],
  ["land", "Land"],
  ["under_5kg", "Air / Sea < 5 kg"],
  ["shein", "SHEIN"],
];

function blank() {
  return {
    id: "",
    name: { en: "", ar: "", fr: "" },                  // legacy compatibility
    card_title: { en: "", ar: "", fr: "" },
    address: { en: "", ar: "", fr: "" },
    chinese_address: "",
    country: "LB",
    country_label: { en: "", ar: "", fr: "" },
    city: "",
    method: "air",
    delivery_eta: "",
    shipping_mark: "",
    warehouse_code: "",
    map_url: "",
    email: "",
    phones: [],
    whatsapp: "",
    contact_label: "",
    notes: { en: "", ar: "", fr: "" },
    sort_order: 100,
    is_active: true,
  };
}

// Phones can come back as either ["96178866746"] OR [{phone, display, label}].
// Normalize to the structured form for the editor.
function normPhones(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((p, i) => {
    if (typeof p === "string") return { id: `p${i}`, phone: p, display: p, label: "" };
    return { id: p.id || `p${i}`, label: p.label || "", phone: p.phone || "", display: p.display || p.phone || "" };
  });
}

// Editor state machine — using `useReducer` keeps the setState calls
// out of the component body, which satisfies React 19's stricter
// `react-hooks/set-state-in-effect` lint rule.
function editingReducer(_state, action) {
  switch (action.type) {
    case "new":
      return { form: blank(), isNew: true };
    case "edit":
      return {
        form: { ...blank(), ...action.warehouse, phones: normPhones(action.warehouse.phones) },
        isNew: false,
      };
    case "patch":
      return _state ? { ..._state, form: action.form } : _state;
    case "close":
      return null;
    default:
      return _state;
  }
}

function AdminWarehousesPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [editing, dispatchEdit] = useReducer(editingReducer, null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const res = await apiFetch("/admin/warehouses");
      const data = await res.json();
      const sorted = (data.warehouses || []).slice().sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
      setItems(sorted);
    } catch (e) {
      toast.error("Failed to load warehouses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const openNew = () => dispatchEdit({ type: "new" });
  const openEdit = (w) => dispatchEdit({ type: "edit", warehouse: w });
  const close = () => dispatchEdit({ type: "close" });

  const onSave = async () => {
    const src = editing.form;
    // `card_title.en` is the canonical English title shown on shipping-only,
    // the website, and the mobile app. We require it explicitly and mirror
    // it into the legacy `name.en` field (in a fresh object — never mutate
    // editing state) so legacy /api/warehouses consumers keep working.
    const enTitle = (src.card_title?.en || "").trim();
    if (!enTitle) {
      toast.error("English card title is required");
      return;
    }
    const f = {
      ...src,
      card_title: { ...(src.card_title || {}), en: enTitle },
      name: { ...(src.name || {}), en: enTitle },
    };
    setSaving(true);
    try {
      const path = editing.isNew ? "/admin/warehouses" : `/admin/warehouses/${f.id}`;
      const method = editing.isNew ? "POST" : "PUT";
      const res = await apiFetch(path, { method, body: JSON.stringify(f) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Save failed");
      toast.success(editing.isNew ? "Warehouse created" : "Warehouse updated");
      close();
      reload();
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Delete warehouse "${id}"?`)) return;
    try {
      const res = await apiFetch(`/admin/warehouses/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Warehouse deleted");
      reload();
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  };

  return (
    <AdminShell
      title={t("warehouses.title")}
      description={t("warehouses.description")}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">{t("warehouses.title")} ({items.length})</h1>
          <p className="text-xs text-white/40 mt-1">
            Single source of truth — mirrors the <span className="font-mono">/shipping-only</span> page, the website, and the mobile app.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold"
          data-testid="warehouse-new"
        >
          <Plus className="w-3.5 h-3.5" /> {t("warehouses.newWarehouse")}
        </button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#F37021]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-white/40 text-sm">
          {t("warehouses.noWarehouses")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="warehouses-list">
          {items.map((w) => {
            const title = w.card_title?.en || w.name?.en || w.id;
            const phones = normPhones(w.phones);
            return (
              <div key={w.id} className="admin-panel p-5 flex flex-col" data-testid={`warehouse-${w.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest text-white/40 uppercase mb-1">
                      {w.country || "—"} · <span className="text-[#F37021]">{w.method || "?"}</span> · {w.id}
                    </div>
                    <h3 className="font-bold text-sm truncate">{title}</h3>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${w.is_active ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/40"}`}>
                    {w.is_active ? t("warehouses.active") : t("warehouses.hidden")}
                  </span>
                </div>

                {w.delivery_eta && (
                  <p className="text-[11px] text-white/50 mt-2">⏱ {w.delivery_eta}</p>
                )}
                {w.shipping_mark && (
                  <p className="text-[11px] text-[#F37021] mt-1 font-mono break-all">↗ {w.shipping_mark}</p>
                )}
                {w.warehouse_code && (
                  <p className="text-[10px] text-white/30 mt-0.5 font-mono">{w.warehouse_code}</p>
                )}
                {w.address?.en && <p className="text-xs text-white/60 mt-2 line-clamp-3">{w.address.en}</p>}
                {w.chinese_address && (
                  <p className="text-[11px] text-white/40 mt-1.5 font-mono line-clamp-2">{w.chinese_address}</p>
                )}
                {phones.length > 0 && (
                  <div className="mt-2 flex flex-col gap-0.5">
                    {phones.map((p, i) => (
                      <span key={i} className="text-[11px] text-white/60 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {p.display || p.phone}
                      </span>
                    ))}
                  </div>
                )}
                {w.whatsapp && (
                  <p className="text-[11px] text-emerald-300 mt-1 flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> WhatsApp: {w.whatsapp}
                  </p>
                )}

                <div className="flex gap-1.5 mt-4 pt-3 border-t border-white/5">
                  <button
                    onClick={() => openEdit(w)}
                    className="flex-1 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-semibold inline-flex items-center justify-center gap-1.5"
                    data-testid={`warehouse-edit-${w.id}`}
                  >
                    <Edit2 className="w-3 h-3" /> {t("warehouses.edit")}
                  </button>
                  <button
                    onClick={() => onDelete(w.id)}
                    className="h-8 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-semibold"
                    data-testid={`warehouse-delete-${w.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <WarehouseForm
          form={editing.form}
          setForm={(f) => dispatchEdit({ type: "patch", form: f })}
          isNew={editing.isNew}
          onClose={close}
          onSave={onSave}
          saving={saving}
        />
      )}

    </AdminShell>
  );
}

function WarehouseForm({ form, setForm, isNew, onClose, onSave, saving }) {
  const set = (patch) => setForm({ ...form, ...patch });
  const setI18n = (field, lang, val) =>
    setForm({ ...form, [field]: { ...(form[field] || {}), [lang]: val } });

  const addPhone = () => set({ phones: [...(form.phones || []), { phone: "", display: "", label: "" }] });
  const updPhone = (i, patch) => set({ phones: form.phones.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const delPhone = (i) => set({ phones: form.phones.filter((_, j) => j !== i) });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto py-8 px-2" data-testid="warehouse-form-modal">
      <form
        onSubmit={(e) => { e.preventDefault(); onSave(); }}
        className="max-w-3xl mx-auto bg-[#141414] border border-white/10 rounded-2xl"
      >
        <header className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#141414] z-10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#F37021]/15 flex items-center justify-center shrink-0">
              <Warehouse className="w-4 h-4 text-[#F37021]" />
            </div>
            <h2 className="font-bold truncate">{isNew ? "New warehouse" : `Edit ${form.id}`}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="warehouse-form-close"><X className="w-5 h-5" /></button>
        </header>

        <div className="p-5 grid sm:grid-cols-2 gap-4">
          {/* Identity */}
          {isNew && <Field label="ID (slug)" value={form.id} onChange={(v) => set({ id: v })} placeholder="cn-air-normal" testid="wh-id" required />}
          <SelectField
            label="Country"
            value={form.country}
            options={COUNTRY_OPTS}
            onChange={(v) => set({ country: v })}
            testid="wh-country"
          />
          <Field label="City" value={form.city} onChange={(v) => set({ city: v })} testid="wh-city" />
          <SelectField
            label="Shipping method"
            value={form.method || "air"}
            options={METHOD_OPTS}
            onChange={(v) => set({ method: v })}
            testid="wh-method"
            hint="Office = customer contact line; SHEIN = SHEIN warehouse"
          />
          <Field label="Delivery ETA" value={form.delivery_eta} onChange={(v) => set({ delivery_eta: v })} placeholder="10 – 15 Days" testid="wh-eta" />
          <Field label="Shipping mark" value={form.shipping_mark} onChange={(v) => set({ shipping_mark: v })} placeholder="JL-2026 (your name)" testid="wh-mark" hint="Code your supplier writes on the box" />
          <Field label="Warehouse code (internal)" value={form.warehouse_code} onChange={(v) => set({ warehouse_code: v })} placeholder="BOB-CN-AIR-1" testid="wh-code" />
          <Field label="Sort order" type="number" value={form.sort_order ?? 100} onChange={(v) => set({ sort_order: Number(v) || 0 })} testid="wh-sort" hint="Lower = appears earlier on shipping-only" />
          <Field label="Map URL (optional)" value={form.map_url} onChange={(v) => set({ map_url: v })} testid="wh-map" />
          <Field label="Email" value={form.email} onChange={(v) => set({ email: v })} testid="wh-email" />
          <Field label="WhatsApp (warehouse-specific)" value={form.whatsapp} onChange={(v) => set({ whatsapp: v })} placeholder="+96178866746" testid="wh-whatsapp" hint="Leave blank for non-customer warehouses" />
          <Field label="Contact person / label" value={form.contact_label} onChange={(v) => set({ contact_label: v })} placeholder="Mohammad Itani" testid="wh-contact" />

          {/* Card title / Country label — bilingual */}
          {["en", "ar", "fr"].map((lang) => (
            <Field
              key={`title-${lang}`}
              label={`Card title (${lang.toUpperCase()})`}
              value={form.card_title?.[lang] || form.name?.[lang] || ""}
              onChange={(v) => { setI18n("card_title", lang, v); setI18n("name", lang, v); }}
              required={lang === "en"}
              testid={`wh-title-${lang}`}
              hint={lang === "en" ? "e.g. 'For Sea Goods'" : undefined}
            />
          ))}
          {["en", "ar", "fr"].map((lang) => (
            <Field
              key={`clbl-${lang}`}
              label={`Country label (${lang.toUpperCase()})`}
              value={form.country_label?.[lang] || ""}
              onChange={(v) => setI18n("country_label", lang, v)}
              testid={`wh-country-label-${lang}`}
            />
          ))}

          {/* Addresses */}
          {["en", "ar", "fr"].map((lang) => (
            <Field
              key={`addr-${lang}`}
              label={`Address (${lang.toUpperCase()})`}
              value={form.address?.[lang] || ""}
              onChange={(v) => setI18n("address", lang, v)}
              testid={`wh-addr-${lang}`}
              multiline
            />
          ))}
          <Field
            label="Chinese address (汉字)"
            value={form.chinese_address}
            onChange={(v) => set({ chinese_address: v })}
            testid="wh-chinese-addr"
            multiline
            hint="Only for China sub-warehouses — used on physical labels"
          />

          {/* Notes / instructions */}
          {["en", "ar", "fr"].map((lang) => (
            <Field
              key={`notes-${lang}`}
              label={`Notes (${lang.toUpperCase()})`}
              value={form.notes?.[lang] || ""}
              onChange={(v) => setI18n("notes", lang, v)}
              testid={`wh-notes-${lang}`}
              multiline
            />
          ))}

          <label className="flex items-center gap-2 col-span-full text-sm">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => set({ is_active: e.target.checked })} data-testid="wh-active" />
            Active (visible on shipping-only page, website, and mobile app)
          </label>

          {/* Phones */}
          <div className="col-span-full">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">Phone numbers</span>
              <button type="button" onClick={addPhone} className="text-xs text-[#F37021] hover:underline" data-testid="wh-add-phone">+ Add</button>
            </div>
            {form.phones?.length ? form.phones.map((p, i) => (
              <div key={i} className="flex gap-2 mb-2" data-testid={`wh-phone-${i}`}>
                <input
                  type="text" placeholder="Label" value={p.label || ""}
                  onChange={(e) => updPhone(i, { label: e.target.value })}
                  className="w-32 h-9 px-2 rounded-lg bg-black/40 border border-white/10 text-xs"
                />
                <input
                  type="text" placeholder="Phone (e.g. +96178866746)" value={p.phone || ""}
                  onChange={(e) => updPhone(i, { phone: e.target.value })}
                  className="flex-1 h-9 px-2 rounded-lg bg-black/40 border border-white/10 text-xs"
                />
                <input
                  type="text" placeholder="Display" value={p.display || ""}
                  onChange={(e) => updPhone(i, { display: e.target.value })}
                  className="flex-1 h-9 px-2 rounded-lg bg-black/40 border border-white/10 text-xs"
                />
                <button type="button" onClick={() => delPhone(i)} className="text-red-400/80 hover:text-red-400 px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            )) : <p className="text-xs text-white/40 italic">No phone numbers yet.</p>}
          </div>
        </div>

        <footer className="flex justify-end gap-2 p-5 border-t border-white/5 sticky bottom-0 bg-[#141414]">
          <button type="button" onClick={onClose} className="h-10 px-5 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={saving} className="h-10 px-6 rounded-full bg-[#F37021] hover:bg-[#E06015] text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50" data-testid="wh-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Create" : "Save"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, hint, testid, type = "text", multiline = false }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">
        {label}{required && <span className="text-red-400 ms-1">*</span>}
      </span>
      {multiline ? (
        <textarea
          rows={2}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="mt-1.5 w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[#F37021] resize-y"
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          data-testid={testid}
          className="mt-1.5 w-full h-10 px-3 rounded-xl bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[#F37021]"
        />
      )}
      {hint && <span className="block text-[11px] text-white/30 mt-1">{hint}</span>}
    </label>
  );
}

function SelectField({ label, value, options, onChange, testid, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full h-10 px-3 rounded-xl bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[#F37021]"
      >
        {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
      {hint && <span className="block text-[11px] text-white/30 mt-1">{hint}</span>}
    </label>
  );
}

export default function AdminWarehousesPage() {
  return (
    <RequireAdmin permission="content.manage">
      <AdminWarehousesPageInner />
    </RequireAdmin>
  );
}
